import React from 'react';
import * as d3 from 'd3';
import { I18n } from 'react-redux-i18n';
import { debounce } from 'lodash';
import { formatTextFloat, formatVolume } from '../../helpers/Text';

const margin = { top: 20, right: 20, bottom: 30, left: 40 };
const MARKER_WIDTH = 35;
const MARKER_HEIGHT = 19;
const TOOLTIP_WIDTH = 108;
const TICKS = 4;
const RATIO = 0.5;
const RED = '#e1515d';
const GREEN = '#37a77d';
const YELLOW = '#facc5c';
const LITTLE_TICKS = 10;
const MEDIUM_TICKS = 30;
const MUCH_TICKS = 50;

const mapTickSize = {
  [LITTLE_TICKS]: 15,
  [MEDIUM_TICKS]: 5,
  [MUCH_TICKS]: 4,
};

const getHalfIndex = (start, end) => {
  const delta = Math.ceil((end - start) / 2);

  return start + delta;
};

class HorizontalBarChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentBar: {},
    };
    this.tooltipRef = React.createRef();
    this.chartRef = React.createRef();
    this.debouncedMouseOver = debounce(this.onMouseOver, 50);
  }

  componentDidMount() {
    this.renderChart();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { data, refPrice } = this.props;
    if (data !== prevProps.data || refPrice !== prevProps.refPrice) {
      this.renderChart();
    }
  }

  getBarWidth = () => {
    const { data } = this.props;
    const length = data.length;
    if (length < LITTLE_TICKS) {
      return mapTickSize[LITTLE_TICKS];
    }

    if (length < MEDIUM_TICKS) {
      return mapTickSize[MEDIUM_TICKS];
    }
    if (length < MUCH_TICKS) {
      return mapTickSize[MUCH_TICKS];
    }

    return 1;
  };

  getHeight = () => {
    const { chartWidth } = this.props;
    return chartWidth * RATIO;
  };

  getTooltipPos = (dx, dy) => {
    if (!this.chartRef.current) {
      return [0, 0];
    }
    const { top, left } = this.chartRef.current.getBoundingClientRect();

    return [left + dx + TOOLTIP_WIDTH / 2 - 8, top + dy];
  };

  onMouseOver = d => {
    this.setState({ currentBar: d });
    const [dx, dy] = this.getTooltipPos(this.x(d.volume), this.y(d.price));
    this.tooltipRef.current.style.display = 'inline-block';
    this.tooltipRef.current.style.left = `${dx}px`;
    this.tooltipRef.current.style.top = `${dy}px`;
  };

  xAxisTickFormat = d => {
    const { isDerivatives } = this.props;
    if (isDerivatives) {
      return formatTextFloat(d, 0);
    }

    return formatTextFloat(formatVolume(d), 0);
  };

  renderChart = () => {
    d3.select('.stat-chart svg').remove();
    const { data, refPrice, chartWidth } = this.props;
    // set the dimensions and margins of the graph
    const width = chartWidth - margin.left - margin.right;
    const height = this.getHeight() - margin.top - margin.bottom;

    // set the ranges
    const y = d3
      .scalePoint()
      .range([height, 0])
      .padding(0.1);
    this.y = y;

    const x = d3.scaleLinear().range([0, width]);
    this.x = x;

    // moves the 'group' element to the top left margin
    const svg = d3
      .select('#horizontal-chart')
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
    this.svg = svg;
    // Scale the range of the data in the domains
    x.domain([
      0,
      d3.max(data, function(d) {
        return d.volume;
      }),
    ]);
    y.domain(
      data.map(function(d) {
        return d.price;
      }),
    );

    // add the x Axis\
    const xAxis = d3
      .axisBottom(x)
      .tickFormat(this.xAxisTickFormat)
      .ticks(TICKS)
      .tickSize(-(this.getHeight() - margin.top - margin.bottom));
    svg
      .append('g')
      .attr('transform', 'translate(0,' + height + ')')
      .attr('class', 'chart-axis chart-axis-x')
      .call(xAxis);

    const yAxis = d3
      .axisLeft(y)
      .tickValues(this.getTickValues())
      .tickFormat(d => formatTextFloat(d))
      .tickSize(0);
    // add the y Axis

    svg
      .append('g')
      .attr('class', 'chart-axis chart-axis-y')
      .call(yAxis)
      .call(g => {
        g.selectAll('.tick text').each(function(d, i) {
          const price = +this.textContent;
          let axisColor = RED;
          if (refPrice < price) {
            axisColor = GREEN;
          }
          if (refPrice === price) {
            axisColor = YELLOW;
          }
          this.style.fill = axisColor;
        });
      });

    // append the rectangles for the bar chart
    svg
      .selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('fill', function(d) {
        if (d.price === refPrice) {
          return YELLOW;
        }

        return d.price < refPrice ? RED : GREEN;
      })
      .attr('width', function(d) {
        return x(d.volume);
      })
      .attr('y', d => {
        return y(d.price) - this.getBarWidth() / 2;
      })
      .attr('height', this.getBarWidth())
      .on('mouseover', this.debouncedMouseOver)
      .on('mouseout', () => {
        this.debouncedMouseOver.cancel();
        this.setState({ currentBar: {} });
      });
    this.drawPriceLine();
  };

  getTickValues = () => {
    const { data } = this.props;
    if (data.length < LITTLE_TICKS) {
      return undefined;
    }
    const values = [];
    values.push(data[0].price);
    const halfIndex = getHalfIndex(0, data.length - 1);
    const leftQuarterIndex = getHalfIndex(0, halfIndex);
    const rightQuarterIndex = getHalfIndex(halfIndex, data.length - 1);
    values.push(data[leftQuarterIndex].price);
    values.push(data[halfIndex].price);
    values.push(data[rightQuarterIndex].price);
    values.push(data[data.length - 1].price);

    return values;
  };

  drawPriceLine = () => {
    const { refPrice, chartWidth } = this.props;
    const dx = 0;
    const dy = this.y(refPrice);
    if (dy === undefined) {
      return;
    }
    const lineStart = [dx, dy];
    const lineEndX = chartWidth - margin.left - margin.right - MARKER_WIDTH;
    const lineEnd = [lineEndX, dy];

    //Draw ref price line
    this.svg
      .append('path')
      .attr('d', d3.line()([lineStart, lineEnd]))
      .attr('stroke', YELLOW)
      .attr('stroke-opacity', 0.2)
      .attr('marker-end', 'url(#rect)')
      .attr('fill', 'none');

    //Draw retangle box
    const rectX = lineEndX;
    const rectY = dy - MARKER_HEIGHT / 2;
    this.svg
      .append('rect')
      .attr('x', rectX)
      .attr('y', rectY)
      .attr('height', MARKER_HEIGHT)
      .attr('width', MARKER_WIDTH)
      .attr('fill', YELLOW)
      .attr('rx', 4);
    //Draw text to Retangle
    this.svg
      .append('text')
      .attr('x', rectX + MARKER_WIDTH / 5)
      .attr('font-size', 10)
      .attr('y', rectY + MARKER_HEIGHT / 2)
      .attr('dominant-baseline', 'middle')
      .attr('dominant-baseline', 'middle')
      .text(() => formatTextFloat(refPrice));
  };

  getTooltipVolume = () => {
    const { currentBar } = this.state;
    const { isDerivatives } = this.props;
    if (!currentBar) {
      return '--';
    }

    return isDerivatives
      ? formatTextFloat(currentBar.volume)
      : formatTextFloat(formatVolume(currentBar.volume));
  };

  render() {
    const { currentBar } = this.state;
    const tooltipClass = Object.keys(currentBar).length
      ? 'd-inline-block'
      : 'd-none';

    return (
      <div className="d-flex flex-column">
        <div ref={this.chartRef} id="horizontal-chart" />
        <div ref={this.tooltipRef} className={`chart-tooltip ${tooltipClass}`}>
          <div className="chart-tooltip-content">
            <div>
              {I18n.t('timeAndSale.tooltip.price', {
                value: currentBar.price ? currentBar.price : '--',
              })}
            </div>
            <div>
              {I18n.t('timeAndSale.tooltip.volume', {
                value: this.getTooltipVolume(),
              })}
            </div>
          </div>
        </div>
        <div className="legend d-flex align-items-center justify-content-center">
          <div className="d-flex mr-6">
            <div className="green" />
            <div className="red" />
          </div>
          {I18n.t('timeAndSale.volume')}
        </div>
      </div>
    );
  }
}

export default HorizontalBarChart;
